import { qualityRead } from "../lib/qualityRead";
import { dataContextGuard } from "../lib/dataContext";
import { fetchJson } from "../lib/api";
import { isLegacyAuthMode } from "../lib/env";
import { supabase } from "../lib/supabase";
import type { SupplierSourceProfile } from "./sourceProfiles";

export type ImportJob = {
  review_file_available?: boolean | null;
  job_id: string;
  status: ImportJobStatus;
  source_type: ImportJobSourceType;
  source_filename: string | null;
  doc_type: string | null;
  supplier: string | null;
  confidence_score: number | null;
  created_at: string | null;
  completed_at: string | null;
  interrupted?: boolean | null;
  completion_pending?: boolean | null;
};

export type ImportJobDetail = ImportJob & {
  parse_result: ImportParseResult | Record<string, unknown> | null;
  parse_warnings: Array<{ code: string; message: string }> | null;
  user_decision: string | null;
  error_message: string | null;
  result_summary: Record<string, unknown> | null;
  // The document this job read, when it is in the data after all.
  present_doc_id?: string | null;
  updated_at: string | null;
};

export type CreateImportResponse = {
  job_id: string;
  status: ImportJobStatus;
  source_filename: string;
  source_type?: ImportJobSourceType;
  doc_type?: string | null;
  supplier?: string | null;
  confidence_score: number;
  parse_result: ImportParseResult | Record<string, unknown>;
  parse_warnings: Array<{ code: string; message: string }>;
  acceptance_policy: ImportAcceptancePolicy;
  detection?: ImportUploadDetection;
  import_capabilities?: UploadImportCapabilities;
  profile_validation?: ImportProfileValidation | null;
  canonical_profile_validation?: ImportProfileValidation | null;
};

export type ImportAcceptanceSignal = {
  source: string;
  code: string;
  decision: string;
  message: string;
  details: Record<string, unknown>;
};

export type ImportAcceptancePolicy = {
  decision: string;
  policy_version: string;
  signals: ImportAcceptanceSignal[];
};

export type ImportJobStatus =
  | "pending"
  | "parsing"
  | "review"
  | "confirmed"
  | "importing"
  | "completed"
  | "failed"
  | "rejected";

export type ImportJobSourceType = "upload" | "mail" | "bulk" | "supplier_batch";

export type ImportSourceStatus = "required" | "optional" | "absent" | "unsupported";

export type ImportProfileValidation = {
  customer_id: string;
  supplier: string;
  tier: string;
  is_explicit: boolean;
  observed_sources: string[];
  required_scope: string[];
  missing_required_sources: string[];
  missing_optional_sources: string[];
  absent_sources_present: string[];
  unsupported_sources_present: string[];
  errors: string[];
  warnings: string[];
  is_valid: boolean;
};

export type GcBatchCapabilities = {
  structured_import_allowed: boolean;
  pdf_staging_allowed: boolean;
  canonical_pdf_import_allowed: boolean;
  delivery_note_pdf_archive_only: boolean;
  required_structured_sources: string[];
  missing_structured_sources: string[];
  degraded_reasons: string[];
};

export type GcSupplierBatchPreview = {
  preview_type: "gc_supplier_batch";
  supplier: "GC";
  batch_id: string | null;
  batch_ref: string;
  ok: boolean;
  source_coverage: {
    structured_file_count: number;
    invoice_pdf_count: number;
    delivery_note_pdf_count: number;
    required_structured_sources_ready: boolean;
    invoice_pdf_ready: boolean;
  };
  profile_validation: ImportProfileValidation | null;
  capabilities: GcBatchCapabilities;
  warnings: string[];
  errors: string[];
};

export type ImportPreviewLine = {
  line_pos: string; description: string; article_code: string | null;
  qty: string | null; uom: string | null; net_unit_price: string | null;
  line_net_amount: string | null; page_no: number | null;
};

export type UploadImportPreview = {
  hilti_cost_center_context?: import("./hilti").HiltiCostCenterContext | null;
  hilti_evidence?: import("./hilti").HiltiInvoiceEvidence;
  document_completeness?: {
    status: string;
    blocking: boolean;
    source_position_count: number;
    parsed_position_count: number;
    printed_total: string | null;
    parsed_total: string | null;
    issues: Array<{code: string; message: string; blocking: boolean; page_no: number | null; line_pos: string | null}>;
  };
  review_file_available?: boolean;
  doc_number?: string | null;
  currency?: string | null;
  lines?: ImportPreviewLine[];
  preview_type?: "upload" | "source_intake";
  intake_kind?: string;
  observed_sources?: string[];
  file_count?: number;
  source_counts?: ImportSourceCounts;
  archive_entry_count?: number;
  doc_type?: string | null;
  page_count?: number | null;
  doc_id?: string | null;
  doc_date?: string | null;
  supplier?: string | null;
  line_count?: number;
  raw_size?: number;
  confidence_score?: number;
  detection?: ImportUploadDetection;
  manual_selection?: ImportManualSelection;
  import_capabilities?: UploadImportCapabilities;
  acceptance_policy?: ImportAcceptancePolicy;
  profile_validation?: ImportProfileValidation | null;
  canonical_profile_validation?: ImportProfileValidation | null;
};

export type ImportSourceCounts = {
  pdf: number;
  ugl: number;
  datanorm: number;
  unsupported: number;
};

export type UploadImportCapabilities = {
  canonical_import_allowed: boolean;
  review_only: boolean;
  review_only_reason: string | null;
  intake_kind: string;
  observed_sources: string[];
  source_counts: ImportSourceCounts;
};

export type ImportDetectionSignal = {
  target: string;
  code: string;
  message: string;
  value: string | null;
  confidence: number;
  source: string;
  evidence: string[];
};

export type ImportUploadDetection = {
  supplier: string | null;
  doc_type: string | null;
  supplier_confidence: number;
  doc_type_confidence: number;
  confidence_score: number;
  review_required: boolean;
  review_reason: string | null;
  signals: ImportDetectionSignal[];
};

export type ImportManualSelection = {
  doc_type: string | null;
  supplier: string | null;
  has_manual_override: boolean;
  fully_manual: boolean;
};

export type ImportParseResult = GcSupplierBatchPreview | UploadImportPreview;

export type ImportQualityIssue = {
  code: string;
  severity: string;
  message: string;
};

export type ParseValidationSummaryRow = {
  check_name: string;
  result: string;
  line_count: number;
  article_count: number;
};

export type ReconciliationFieldSummary = {
  field_name: string;
  total_pairs: number;
  exact_count: number;
  rounding_count: number;
  mismatch_count: number;
  missing_count: number;
  exact_rate: number;
  mismatch_rate: number;
};

export type ImportQualityReport = {
  customer_id: string;
  supplier: string;
  run_id: string;
  status: string;
  source_profile: SupplierSourceProfile;
  acceptance: ImportAcceptancePolicy;
  issues: ImportQualityIssue[];
  parse_validation: {
    available: boolean;
    summary: ParseValidationSummaryRow[];
    flagged_line_count: number;
    flagged_article_count: number;
    error: string | null;
  };
  reconciliation: {
    available: boolean;
    matched_doc_count: number;
    ugl_only_doc_count: number;
    pdf_only_doc_count: number;
    matched_line_count: number;
    ugl_only_line_count: number;
    pdf_only_line_count: number;
    unmatched_line_rate: number;
    field_summaries: ReconciliationFieldSummary[];
    error: string | null;
  };
  set_price_qa: {
    available: boolean;
    status_counts: Record<string, number>;
    reviewed_set_count: number;
    issue_set_count: number;
    samples: Array<Record<string, unknown>>;
    error: string | null;
  };
};

export type ImportQualityReviewKind =
  | "parse_validation_line"
  | "reconciliation_supplier_case"
  | "set_price_qa_set";

export type ImportQualityReviewStatus =
  | "all"
  | "open"
  | "resolved"
  | "action_required";

export type ImportQualityReviewAction =
  | "resolve_plausible"
  | "mark_correction_required"
  | "reopen";

export type ImportQualityReviewDecision = {
  event_id: string;
  action: ImportQualityReviewAction;
  previous_status: string;
  status: string;
  comment: string | null;
  actor_user_id?: string;
  actor_label?: string | null;
  created_at: string;
};

export type ImportQualityReviewFinding = {
  finding_key: string;
  finding_kind: ImportQualityReviewKind;
  supplier: string;
  title: string;
  summary: string;
  severity: string;
  effective_status: string;
  active: boolean;
  assessment_token: string;
  evidence_snapshot: Record<string, unknown>;
  latest_decision?: ImportQualityReviewDecision | null;
  history_event_id?: string;
};

export type ImportQualityReviewResponse = {
  next_open_key?: string | null;
  counts?: { open_count: number; action_required_count: number; resolved_count: number } | null;
  supplier: string;
  kind: ImportQualityReviewKind;
  status: ImportQualityReviewStatus;
  total: number;
  limit: number;
  offset: number;
  items: ImportQualityReviewFinding[];
};

export type ImportQualityReviewDecisionResponse = {
  event: ImportQualityReviewDecision;
  item: ImportQualityReviewFinding;
};

export type ImportQualityReviewHistoryEvent = ImportQualityReviewDecision & {
  request_id: string;
  finding_key: string;
  finding_kind: ImportQualityReviewKind;
  supplier: string;
  assessment_token: string;
  title: string;
  summary: string;
  evidence_snapshot: Record<string, unknown>;
  active: boolean;
};

export type ImportQualityReviewHistoryResponse = {
  supplier: string;
  kind: ImportQualityReviewKind;
  finding_key: string | null;
  total: number;
  limit: number;
  offset: number;
  events: ImportQualityReviewHistoryEvent[];
};

export async function uploadImport(
  file: File,
  docType: string | null,
  supplier: string | null,
): Promise<CreateImportResponse> {
  const checkContext = dataContextGuard();
  let accessToken: string | undefined;
  if (!isLegacyAuthMode()) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    accessToken = session?.access_token;
  }

  const formData = new FormData();
  formData.append("file", file);
  if (docType) formData.append("doc_type", docType);
  if (supplier) formData.append("supplier", supplier);

  const headers: Record<string, string> = {};
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  checkContext();
  const response = await fetch("/api/imports", {
    method: "POST",
    credentials: "same-origin",
    headers,
    body: formData,
  });

  checkContext();
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = (await response.json()) as { detail?: string };
      if (body.detail) detail = body.detail;
    } catch { /* ignore */ }
    throw new Error(detail);
  }

  const result = await response.json() as CreateImportResponse;
  checkContext();
  return result;
}

export async function createGcBatchPreview(
  batchId: string,
): Promise<CreateImportResponse> {
  return fetchJson<CreateImportResponse>("/api/imports/gc-batches/preview", {
    method: "POST",
    body: JSON.stringify({ batch_id: batchId }),
  });
}

/** `mine` lists only the caller's own uploads, newest first (#631). */
export async function listImportJobs(options: { mine?: boolean; limit?: number } = {}): Promise<ImportJob[]> {
  const params = new URLSearchParams();
  if (options.mine) params.set("mine", "true");
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  const query = params.toString();
  const result = await fetchJson<{ jobs: ImportJob[] }>(`/api/imports${query ? `?${query}` : ""}`);
  return result.jobs;
}

export async function getImportQualityReport(
  supplier: string,
): Promise<ImportQualityReport> {
  const params = new URLSearchParams({ supplier });
  return qualityRead<ImportQualityReport>(`/api/imports/quality?${params.toString()}`);
}

export async function getImportQualityReview(args: {
  supplier: string;
  kind: ImportQualityReviewKind;
  status: ImportQualityReviewStatus;
  limit: number;
  offset: number;
  findingKey?: string | null;
  documentId?: string | null;
  signal?: AbortSignal;
}): Promise<ImportQualityReviewResponse> {
  const params = new URLSearchParams({
    supplier: args.supplier,
    kind: args.kind,
    status: args.status,
    limit: String(args.limit),
    offset: String(args.offset),
  });
  if (args.documentId) params.set("document_id", args.documentId);
  if (args.findingKey) params.set("finding_key", args.findingKey);
  return qualityRead<ImportQualityReviewResponse>(`/api/imports/quality/review?${params}`, args.signal);
}

export async function recordImportQualityReviewDecision(args: {
  supplier: string;
  findingKind: ImportQualityReviewKind;
  findingKey: string;
  assessmentToken: string;
  action: ImportQualityReviewAction;
  comment: string;
  requestId: string;
}): Promise<ImportQualityReviewDecisionResponse> {
  return fetchJson<ImportQualityReviewDecisionResponse>(
    "/api/imports/quality/review/decisions",
    {
      method: "POST",
      body: JSON.stringify({
        supplier: args.supplier,
        finding_kind: args.findingKind,
        finding_key: args.findingKey,
        assessment_token: args.assessmentToken,
        action: args.action,
        comment: args.comment,
        request_id: args.requestId,
      }),
    },
  );
}

export async function getImportQualityReviewHistory(args: {
  supplier: string;
  kind: ImportQualityReviewKind;
  limit: number;
  offset: number;
  findingKey?: string | null;
  eventId?: string | null;
  signal?: AbortSignal;
}): Promise<ImportQualityReviewHistoryResponse> {
  const params = new URLSearchParams({
    supplier: args.supplier,
    kind: args.kind,
    limit: String(args.limit),
    offset: String(args.offset),
  });
  if (args.findingKey) params.set("finding_key", args.findingKey);
  if (args.eventId) params.set("event_id", args.eventId);
  return qualityRead<ImportQualityReviewHistoryResponse>(`/api/imports/quality/review/history?${params}`, args.signal);
}

export type ImportJobOutcome = {
  job_id: string;
  outcome: NonNullable<BatchImportEvent["outcome"]>;
  message: string;
  done: boolean;
};

// Where each queued file of a batch stands, worded like the batch stream.
export async function getImportOutcomes(jobIds: string[]): Promise<ImportJobOutcome[]> {
  const result = await fetchJson<{ items: ImportJobOutcome[] }>("/api/imports/outcomes", {
    method: "POST",
    body: JSON.stringify({ job_ids: jobIds }),
  });
  return result.items;
}

export async function getImportJob(jobId: string): Promise<ImportJobDetail> {
  return fetchJson<ImportJobDetail>(`/api/imports/${jobId}`);
}

export async function confirmImportJob(
  jobId: string,
  decision: "confirm" | "reject",
): Promise<{ job_id: string; status: string; decision: string }> {
  return fetchJson(`/api/imports/${jobId}/confirm`, {
    method: "POST",
    body: JSON.stringify({ decision }),
  });
}

export type BatchImportEvent = {
  type: "start" | "processing" | "result" | "finalizing" | "done";
  total?: number;
  index?: number;
  name?: string;
  outcome?: "completed" | "review" | "duplicate" | "rejected" | "pending" | "queued" | "interrupted";
  message?: string;
  job_id?: string;
  input_index?: number;
  input_done?: boolean;
  deferred_jobs?: number;
  read_model_refresh?: "completed" | "failed";
  existing_source?: { file_ref: string; imported_at: string | null; doc_id: string | null } | null;
};

export async function uploadImportBatch(
  files: File[], docType: string | null, supplier: string | null,
  signal: AbortSignal, onEvent: (event: BatchImportEvent) => void,
): Promise<void> {
  const checkContext = dataContextGuard();
  const headers: Record<string, string> = {};
  if (!isLegacyAuthMode()) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  }
  const body = new FormData();
  files.forEach((file) => body.append("files", file));
  if (docType) body.append("doc_type", docType);
  if (supplier) body.append("supplier", supplier);
  checkContext();
  const response = await fetch("/api/imports/batch", {
    method: "POST", credentials: "same-origin", headers, body, signal,
  });
  checkContext();
  if (!response.ok) {
    const error = await response.json().catch(() => null) as { detail?: unknown } | null;
    throw new Error(typeof error?.detail === "string" ? error.detail : "Upload konnte nicht gestartet werden.");
  }
  if (!response.body) throw new Error("Fortschritt konnte nicht gelesen werden.");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let completed = false;
  try {
    while (true) {
      const { value, done } = await reader.read();
      checkContext();
      buffer += decoder.decode(value, { stream: !done });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.trim()) continue;
        const event = JSON.parse(line) as BatchImportEvent;
        if (event.type === "done") completed = true;
        onEvent(event);
      }
      if (done) break;
    }
    if (!completed) throw new Error("Verbindung unterbrochen. Den letzten Dateistatus bitte im Importverlauf prüfen.");
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}


export async function recheckImportJob(jobId: string, docType: string, supplier: string, file?: File) {
  const checkContext = dataContextGuard();
  const form = new FormData();
  form.append("doc_type", docType);
  form.append("supplier", supplier);
  if (file) form.append("file", file);
  const headers: Record<string, string> = {};
  if (!isLegacyAuthMode()) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) headers.Authorization = `Bearer ${session.access_token}`;
  }
  checkContext();
  const response = await fetch(`/api/imports/${jobId}/recheck`, { method: "POST", credentials: "same-origin", headers, body: form });
  checkContext();
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(typeof body.detail === "string" ? body.detail : "Die erneute Prüfung konnte nicht abgeschlossen werden.");
  }
  const result = await response.json();
  checkContext();
  return result;
}

export function reopenImportJob(jobId: string) {
  return fetchJson<{job_id: string; status: string}>(`/api/imports/${jobId}/reopen`, { method: "POST" });
}

export type ImportAttentionItem = {
  job_id: string;
  filename: string | null;
  created_at: string | null;
  state: "review" | "missing_original" | "failed" | "interrupted";
};
export type ImportStatus = {
  inventory: {
    last_import_at: string | null; file_count: number; undated_count: number;
    groups: Array<{ format: string; supplier: string | null; file_count: number;
      document_count: number; last_import_at: string | null; undated_count: number;
      excluded_count: number; unparsed_count: number }>;
  };
  states: Partial<Record<ImportJobStatus | "missing_original" | "interrupted" | "completion_pending", { count: number; job_id: string }>>;
  finalization?: { deferred_jobs: number; idle_seconds: number; stranded: boolean };
  // Every job waiting for a person, newest first, whatever its age.
  attention?: ImportAttentionItem[];
  latest_upload: { job_id: string; created_at: string; status: ImportJobStatus; actor: string | null; already_present?: boolean } | null;
};
export function finishStrandedImport(): Promise<{
  status: string;
  deferred_jobs: number;
  without_original?: number;
}> {
  return fetchJson("/api/imports/finalize", { method: "POST" });
}

export function getImportStatus(): Promise<ImportStatus> {
  return fetchJson<ImportStatus>("/api/imports/status");
}

export type QualityReviewDocument = { group_key: string; document_id: string | null; document_number: string | null; document_date: string | null; total_findings: number; open_count: number; action_required_count: number; resolved_count: number; first_finding_key: string };
export function getQualityReviewDocuments(supplier: string, status: ImportQualityReviewStatus, offset: number, signal?: AbortSignal) {
  const params = new URLSearchParams({ supplier, status, kind: 'parse_validation_line', group_by: 'document', limit: '25', offset: String(offset) });
  return qualityRead<{ items: QualityReviewDocument[]; total: number; offset: number; limit: number }>(`/api/imports/quality/review?${params}`, signal);
}
